/** The packed atlas: `assets.json` plus the sheet bitmaps.
 *
 * Two hard requirements, both about not destroying the art:
 *
 *  - Every texture samples NEAREST with mipmaps off. A linear sample between
 *    two texels of 16-bit pixel art is mush, and a mipmap is the same mistake
 *    made ahead of time.
 *  - The TERRAIN sheets are kept as `ImageBitmap`s after upload, because the
 *    terrain compositor needs to read pixels on the CPU to build the transition
 *    tiles the pre-baked pair atlases do not cover (`world/terrain.ts`).
 *
 * Every other sheet's bitmap is closed as soon as a renderer has uploaded it
 * (`prepare`). Measured 2026-09-12: 22 sheets of 4096x4096 decode to ~1.25 GB
 * of renderer-process malloc, held for the life of the page, and the game
 * loaded the atlas twice - a 4 GB renderer on the adventure map, which is what
 * killed the tab on a 16 GB laptop (Chrome's PartitionAlloc OOM, SIGTRAP, four
 * times in one afternoon). A second WebGL context that needs a released sheet
 * decodes it again from the browser's HTTP cache, which is cheap next to
 * keeping a gigabyte resident.
 *
 * Textures are cached by key; a frame is a view onto a shared `TextureSource`,
 * so 8732 keys cost 22 GPU textures.
 */

import { ImageSource, Rectangle, Texture } from "pixi.js";
import type { Renderer } from "pixi.js";
import type { AtlasManifest, FrameMeta, ObjectMeta, SheetMeta } from "./world/frames.js";

export type { AtlasManifest, FrameMeta, ObjectMeta, SheetMeta } from "./world/frames.js";

interface ObjectsFile {
  readonly meta?: { readonly objects?: Readonly<Record<string, ObjectMeta>> };
}

export interface AtlasOptions {
  /** Where `assets.json` and `sheets/` live. Defaults to `/assets`. */
  readonly baseUrl?: string;
}

/** The one sheet group whose pixels are read on the CPU (`world/terrain.ts`). */
const CPU_GROUP = "terrain";

/** A sheet's pack group; old manifests carry it only as the id's prefix. */
function groupOf(meta: SheetMeta): string {
  return meta.group ?? meta.id.split("-")[0] ?? meta.id;
}

export class Atlas {
  private readonly sources = new Map<string, ImageSource>();
  private readonly bitmaps = new Map<string, ImageBitmap>();
  private readonly textures = new Map<string, Texture>();
  private readonly frames: Readonly<Record<string, FrameMeta>>;
  private readonly objects: Readonly<Record<string, ObjectMeta>>;
  private readonly sheetMeta: readonly SheetMeta[];
  /** Where each sheet comes from; decoded on demand by `prepare`. */
  private readonly sheetUrls: ReadonlyMap<string, string>;
  /** Renderer uids each sheet has been uploaded into. */
  private readonly uploadedTo = new Map<string, Set<number>>();
  /** Sheets a texture has ever been asked from, for the group probes in `qa/`. */
  private readonly touched = new Set<string>();
  /** `prepare` calls are serialised: two contexts mounting at once must not
   *  close a bitmap the other is about to upload from. */
  private preparing: Promise<void> = Promise.resolve();

  private constructor(
    manifest: AtlasManifest,
    objects: Readonly<Record<string, ObjectMeta>>,
    sheetUrls: ReadonlyMap<string, string>,
  ) {
    this.frames = manifest.keys;
    this.objects = objects;
    this.sheetMeta = manifest.sheets;
    this.sheetUrls = sheetUrls;
    for (const s of manifest.sheets) {
      // No resource yet: the sheet is decoded by `prepare`, for the screen
      // that declares its group. The manifest carries the dimensions, which is
      // all a frame's UVs need.
      const source = new ImageSource({
        width: s.w,
        height: s.h,
        scaleMode: "nearest",
        autoGenerateMipmaps: false,
        label: `atlas:${s.id}`,
      });
      // Pixi turns garbage collection ON for image sources: an idle sheet is
      // unloaded after ~60 s and re-uploaded from `resource` on its next use.
      // With the CPU copy released that re-upload has nothing to read, so the
      // sheets stay resident in the context for as long as the context lives.
      source.autoGarbageCollect = false;
      this.sources.set(s.id, source);
    }
  }

  static async load(opts: AtlasOptions = {}): Promise<Atlas> {
    const base = (opts.baseUrl ?? "/assets").replace(/\/+$/, "");
    const manifest = await fetchJson<AtlasManifest>(`${base}/assets.json`);

    // objects.json is the only place the tile-space footprint of a map object
    // survives; it is optional so the renderer still boots without it.
    let objects: Readonly<Record<string, ObjectMeta>> = {};
    try {
      const of = await fetchJson<ObjectsFile>(`${base}/objects.json`);
      objects = of.meta?.objects ?? {};
    } catch {
      objects = {};
    }

    const urls = new Map<string, string>();
    for (const s of manifest.sheets) urls.set(s.id, `${base}/${s.file.replace(/^\/+/, "")}`);
    return new Atlas(manifest, objects, urls);
  }

  /**
   * Decode and upload every sheet of `groups` into `renderer`'s context, then
   * release the CPU copy of each one that nothing reads on the CPU.
   *
   * Call it once per mounted renderer, before the first frame, naming every
   * group the screen draws from (`qa/` probes read `touchedSheets` to keep the
   * lists honest). A sheet whose bitmap an earlier context released is decoded
   * again from the browser's HTTP cache for this one - about a second for a
   * screen's worth, against a gigabyte kept resident. A group the screen did
   * not declare and then draws from fails loudly in `texture`.
   */
  prepare(renderer: Renderer, groups: readonly string[]): Promise<void> {
    const run = this.preparing.then(() => this.prepareNow(renderer, groups));
    this.preparing = run.catch(() => undefined);
    return run;
  }

  private async prepareNow(renderer: Renderer, groups: readonly string[]): Promise<void> {
    const wanted = new Set(groups);
    await Promise.all(this.sheetMeta.map(async (meta) => {
      const group = groupOf(meta);
      if (!wanted.has(group)) return;
      const source = this.sources.get(meta.id);
      if (source === undefined) return;
      let uids = this.uploadedTo.get(meta.id);
      if (uids === undefined) { uids = new Set(); this.uploadedTo.set(meta.id, uids); }
      let bmp = this.bitmaps.get(meta.id);
      if (!uids.has(renderer.uid)) {
        if (bmp === undefined) {
          const url = this.sheetUrls.get(meta.id);
          if (url === undefined) throw new Error(`atlas: sheet ${meta.id} has no source url`);
          bmp = await decodeSheet(meta.id, url);
          this.bitmaps.set(meta.id, bmp);
        }
        source.resource = bmp;
        renderer.texture.initSource(source);
        uids.add(renderer.uid);
      }
      if (group === CPU_GROUP || bmp === undefined) return;
      this.bitmaps.delete(meta.id);
      bmp.close();
      // A closed ImageBitmap is 0x0; leaving it on the source would make any
      // later upload silently produce an empty texture. Null fails loudly.
      source.resource = null as unknown as ImageBitmap;
    }));
  }

  get sheets(): readonly SheetMeta[] { return this.sheetMeta; }

  /** Sheets `texture` has been asked for so far, sorted. For the group probes. */
  get touchedSheets(): readonly string[] { return [...this.touched].sort(); }

  has(key: string): boolean { return this.frames[key] !== undefined; }

  frame(key: string): FrameMeta | null { return this.frames[key] ?? null; }

  /** The first key of `candidates` that exists, or `null`. */
  resolve(candidates: readonly string[]): string | null {
    for (const c of candidates) if (this.has(c)) return c;
    return null;
  }

  texture(key: string): Texture {
    const cached = this.textures.get(key);
    if (cached !== undefined) return cached;
    const f = this.frames[key];
    if (f === undefined) throw new Error(`atlas: no frame "${key}"`);
    const source = this.sources.get(f.sheet);
    if (source === undefined) throw new Error(`atlas: frame "${key}" names sheet "${f.sheet}"`);
    this.touched.add(f.sheet);
    if ((this.uploadedTo.get(f.sheet)?.size ?? 0) === 0) {
      throw new Error(
        `atlas: sheet "${f.sheet}" was never prepared - the presenter drawing "${key}" `
        + "must name its group in Atlas.prepare",
      );
    }
    const tex = new Texture({
      source,
      frame: new Rectangle(f.x, f.y, f.w, f.h),
      label: key,
    });
    this.textures.set(key, tex);
    return tex;
  }

  tryTexture(key: string): Texture | null {
    return this.has(key) ? this.texture(key) : null;
  }

  objectMeta(id: string): ObjectMeta | null {
    const bare = id.startsWith("obj.") ? id.slice(4) : id;
    return this.objects[bare] ?? null;
  }

  /** CPU-side pixels, for the terrain compositor. Only the terrain sheets stay
   *  resident once `prepare` has run; every other sheet answers `null`. */
  bitmap(sheetId: string): ImageBitmap | null {
    return this.bitmaps.get(sheetId) ?? null;
  }

  /**
   * Sampling for every sheet at once. The adventure presenter asks for
   * `"linear"` while its frame is minified (`ZOOM_OUT_STEPS` in `coords.ts`,
   * where the sampling argument lives) and `"nearest"` otherwise; a sheet already
   * in the asked-for mode is left alone, so this is cheap to call per frame.
   */
  setScaleMode(mode: "nearest" | "linear"): void {
    for (const source of this.sources.values()) {
      if (source.scaleMode === mode) continue;
      source.scaleMode = mode;
      source.style.update();
    }
  }

  destroy(): void {
    for (const t of this.textures.values()) t.destroy(false);
    this.textures.clear();
    for (const s of this.sources.values()) s.destroy();
    this.sources.clear();
    for (const b of this.bitmaps.values()) b.close();
    this.bitmaps.clear();
    this.uploadedTo.clear();
  }
}

async function decodeSheet(id: string, url: string): Promise<ImageBitmap> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`atlas: sheet ${id} -> HTTP ${res.status}`);
  return await createImageBitmap(await res.blob());
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`atlas: ${url} -> HTTP ${res.status}`);
  return await res.json() as T;
}
